import { type FC, useEffect, useState } from 'react';
import { Button, Modal, Text, Textarea } from '@g2u/ui-kit';
import { Form } from 'antd';

type Props = {
  isOpen: boolean;
  id: number;
  onCancel: () => void;
  comments?: string | null;
  onSave: (comments: string, onDone: () => void) => void;
  isSaving?: boolean;
};

type FormFields = {
  comments: string;
};

export const CommentsModal: FC<Props> = ({ isOpen, id, onCancel, comments, onSave, isSaving }) => {
  const [form] = Form.useForm<FormFields>();
  const [isEditing, setIsEditing] = useState(false);
  const [displayComments, setDisplayComments] = useState(comments ?? '');

  // Modal.$ does set destroyOnClose, which would reset the form field itself, but isEditing
  // and displayComments live in this component, not inside the Modal's content — they survive
  // its remount untouched. Without resetting them here, reopening could land back in edit mode
  // showing a different subscription's draft. displayComments (not a form watch) drives the
  // read view so a just-completed save shows immediately, without waiting on the comments prop
  // to refetch.
  useEffect(() => {
    if (isOpen) {
      const value = comments ?? '';
      form.setFieldsValue({ comments: value });
      setDisplayComments(value);
      setIsEditing(false);
    }
  }, [isOpen, id, comments, form]);

  const handleEdit = () => {
    // The textarea only exists in the DOM once isEditing is true, so it needs its value set at
    // the moment it mounts rather than trusting a setFieldsValue call from before it existed.
    form.setFieldsValue({ comments: displayComments });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    form.setFieldsValue({ comments: displayComments });
    setIsEditing(false);
  };

  const handleSave = () => {
    const value = form.getFieldValue('comments');
    onSave(value, () => {
      setDisplayComments(value);
      setIsEditing(false);
    });
  };

  return (
    <Modal.$
      open={isOpen}
      title="Comments"
      onCancel={onCancel}
      dataTestId="comments-modal"
      footer={
        isEditing ? (
          <>
            <Button.$ dataTestId="comments-cancel-edit" onClick={handleCancelEdit}>
              Cancel
            </Button.$>
            <Button.$
              dataTestId="comments-save"
              type={Button.Type.Primary}
              loading={isSaving}
              onClick={handleSave}
            >
              Save
            </Button.$>
          </>
        ) : (
          <Button.$ dataTestId="comments-edit" onClick={handleEdit}>
            Edit
          </Button.$>
        )
      }
    >
      <Form form={form} layout="vertical">
        {isEditing ? (
          <Textarea.$ name="comments" initialValue={displayComments} />
        ) : (
          <Text.$ style={{ whiteSpace: 'pre-wrap' }}>{displayComments || 'N/A'}</Text.$>
        )}
      </Form>
    </Modal.$>
  );
};
